import { useCallback, useMemo } from 'react'
import { Link, useSearchParams } from 'react-router'
import { useAree } from '../../servizi/sessione.tsx'
import { Attesa, Avviso, Intestazione, Vuoto } from '../../componenti/Pezzi.tsx'
import {
  Bottone, Etichetta, EtichettaArea, Paginazione, Riquadro, Scelta, Spunta, Tabella,
} from '../../componenti/base.tsx'
import { LIMITE, conteggio, giorno, usaElenco } from '../../componenti/elenco.tsx'
import { tonoIntervento } from '../scheda/comune.ts'
import { durataBreve } from '../../servizi/regole-intervento.ts'

/** Stati che si scelgono dal filtro, con il nome che leggono gli operatori. */
const STATI: { valore: string; etichetta: string }[] = [
  { valore: 'bozza', etichetta: 'Da completare' },
  { valore: 'completo', etichetta: 'Completi' },
  { valore: 'annullato', etichetta: 'Annullati' },
  { valore: 'storico_incompleto', etichetta: 'Dal vecchio archivio' },
]

const NOME_STATO: Record<string, string> = {
  bozza: 'da completare', completo: 'completo', annullato: 'annullato', storico_incompleto: 'vecchio archivio',
}

/**
 * Senza filtro di stato l'elenco mostra tutto tranne gli interventi del vecchio archivio: entrati
 * senza durata (il vecchio sistema non la registrava), non sono cose da fare e si vedono solo
 * scegliendo «Dal vecchio archivio» (segnalazione di CIDIS del 19/10/2026).
 */
const SENZA_VECCHIO_ARCHIVIO = 'bozza,completo,annullato'

/**
 * Elenco degli interventi.
 *
 * I filtri stanno nell'indirizzo, cosi' un collegamento porta all'elenco gia' filtrato: il
 * cruscotto rimanda a `/interventi?stato=bozza&miei=true`, gli interventi da completare di chi
 * guarda. Ogni bozza ha il comando «Completa», che apre la registrazione dell'intervento invece
 * della scheda della persona; compare solo a chi puo' modificarlo.
 */
export function Interventi() {
  type Riga = {
    id: string; persona_id: string; data: string; durata_minuti: number | null
    tipologia: string; area_codice: string; stato: string; modificabile: boolean
    operatore_nome: string | null; operatore_cognome: string | null
  }
  const aree = useAree()
  const [parametri, setParametri] = useSearchParams()
  const area = parametri.get('area') ?? ''
  const statoGrezzo = parametri.get('stato') ?? ''
  const stato = STATI.some((s) => s.valore === statoGrezzo) ? statoGrezzo : ''
  const miei = parametri.get('miei') === 'true'

  // D-05: la tendina Area filtra gli elenchi. Ogni filtro cambia il percorso e usaElenco riporta la
  // finestra a zero.
  const aggiorna = useCallback((chiave: 'area' | 'stato' | 'miei', valore: string) => {
    setParametri((attuali) => {
      const nuovi = new URLSearchParams(attuali)
      if (valore) nuovi.set(chiave, valore); else nuovi.delete(chiave)
      return nuovi
    }, { replace: true })
  }, [setParametri])

  const percorso = useMemo(() => {
    const q = new URLSearchParams()
    if (area) q.set('area', area)
    q.set('stato', stato || SENZA_VECCHIO_ARCHIVIO)
    if (miei) q.set('miei', 'true')
    return `/interventi?${q.toString()}`
  }, [area, stato, miei])
  const { dati, errore, finestra } = usaElenco<Riga>(percorso)
  const conFiltri = !!(area || stato || miei)

  const vuoto = stato === 'bozza'
    ? (miei ? 'Nessun intervento da completare: le tue bozze sono tutte chiuse.' : 'Nessun intervento da completare fra quelli che puoi vedere.')
    : miei ? 'Nessun intervento tuo con questi filtri.'
      : area ? 'Nessun intervento dell’area fra quelli che puoi vedere.' : 'Nessun intervento fra quelli che puoi vedere.'

  return (
    <>
      <Intestazione titolo="Interventi" sottotitolo={conteggio(finestra.totale, 'intervento', 'interventi')} />

      <Riquadro titolo="Filtri" azioni={conFiltri
        ? <Bottone variante="discreto" dimensione="piccolo"
                   onClick={() => setParametri(new URLSearchParams(), { replace: true })}>Azzera i filtri</Bottone>
        : undefined}>
        <div className="filtri-report">
          <Scelta etichetta="Stato" valore={stato} cambia={(v) => aggiorna('stato', v)}
                  vuota="Tutti, senza il vecchio archivio" opzioni={STATI} />
          <Scelta etichetta="Area" valore={area} cambia={(v) => aggiorna('area', v)} vuota="Tutte le aree"
                  opzioni={aree.aree.filter((a) => a.attiva).map((a) => ({ valore: a.codice, etichetta: a.nome }))} />
          <Spunta etichetta="Solo i miei" valore={miei} cambia={(v) => aggiorna('miei', v ? 'true' : '')}
                  aiuto="Quelli che hai registrato o di cui sei l’operatore." />
        </div>
      </Riquadro>

      {stato === 'storico_incompleto' && (
        <Avviso>
          Interventi portati dal vecchio archivio. Il vecchio sistema non registrava la durata, che resta
          a zero: non si completano e non contano fra le cose da fare.
        </Avviso>
      )}
      {stato === 'bozza' && (
        <Avviso>
          Bozze salvate e interventi nati da una mediazione svolta. «Completa» apre l’intervento con i
          dati già inseriti, per aggiungere quelli che mancano.
        </Avviso>
      )}

      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {!errore && !dati && <Attesa />}
      {dati && (
        <Riquadro denso>
          <Tabella intestazioni={['Data', 'Area', 'Tipologia', 'Durata', 'Operatore', 'Stato',
                     <span key="comandi" className="solo-lettore">Comandi</span>]}
                   vuoto={<Vuoto testo={vuoto} />}>
            {dati.map((i) => (
              <tr key={i.id}>
                <td data-etichetta="Data">{giorno(i.data)}</td>
                <td data-etichetta="Area"><EtichettaArea codice={i.area_codice} colore={aree.coloreDi(i.area_codice)} /></td>
                <td data-etichetta="Tipologia"><Link to={`/persone/${i.persona_id}`}>{i.tipologia}</Link></td>
                <td data-etichetta="Durata" className="mono">
                  {/* Senza durata (facoltativa dalla 551, o storico che non la registrava): il trattino. */}
                  {durataBreve(i.durata_minuti)}
                </td>
                <td data-etichetta="Operatore">{i.operatore_cognome ? `${i.operatore_cognome} ${i.operatore_nome}` : '—'}</td>
                <td data-etichetta="Stato">
                  <Etichetta tono={tonoIntervento(i.stato)}>{NOME_STATO[i.stato] ?? i.stato}</Etichetta>
                </td>
                <td data-etichetta="Comandi">
                  {i.stato === 'bozza' && i.modificabile && (
                    <Link className="bottone bottone-primario bottone-piccolo"
                          to={`/persone/${i.persona_id}/intervento/${i.id}`}>
                      Completa
                    </Link>
                  )}
                </td>
              </tr>
            ))}
          </Tabella>
          <Paginazione salta={finestra.salta} limite={LIMITE} totale={finestra.totale}
                       ricevute={dati.length} cambia={finestra.cambia} />
        </Riquadro>
      )}
    </>
  )
}
